import allCountries from 'intl-tel-input/data'

/**
 * The offline country dictionary (PLAN.md §11, §14).
 *
 * `/api/v1/meta/countries` is authoritative — it is the CRM's own list, already
 * filtered to jurisdictions we can onboard from — but it is also a network call
 * to a third party on the very first field of the funnel. When it fails the
 * picker has nothing to show, and step 1 becomes a dead end for everyone: no
 * country, no account. That is a whole funnel taken down by an upstream we do
 * not control.
 *
 * So the codes ship in the bundle too. This list is a *fallback for names*, not
 * a second opinion on eligibility: restricted jurisdictions are still rejected
 * by `POST /account`, which checks `CountryService.isRestricted` server-side
 * regardless of what the picker offered. The worst case is that someone picks a
 * country the CRM would have hidden and is told no one field later — strictly
 * better than being told nothing and being unable to continue at all.
 *
 * Names are not hard-coded. `Intl.DisplayNames` has them for every locale the
 * funnel serves, which is how the list stays correct in Thai and Japanese
 * without shipping five translations of 244 country names.
 */

export interface CountryOption {
  /** ISO 3166-1 alpha-2, uppercase — the form the CRM speaks. */
  code: string
  /** Localised display name. */
  name: string
  /** E.164 calling code, digits only, no leading `+`. */
  dialCode: string
}

/** Regional indicator pair, e.g. `TH` → 🇹🇭. */
export function flagEmoji(code: string): string {
  return code
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)))
}

/** Built once per locale: 244 `Intl.DisplayNames` lookups is not per-render work. */
const cache = new Map<string, CountryOption[]>()

/**
 * Every country `intl-tel-input` knows a dial code for, named in `locale` and
 * sorted the way that locale sorts.
 */
export function countryOptions(locale = 'en'): CountryOption[] {
  const hit = cache.get(locale)
  if (hit) return hit

  // An unknown locale tag throws rather than falling back, and a picker is not
  // worth crashing a screen over.
  let display: Intl.DisplayNames
  let collator: Intl.Collator
  try {
    display = new Intl.DisplayNames([locale], { type: 'region' })
    collator = new Intl.Collator(locale)
  } catch {
    display = new Intl.DisplayNames(['en'], { type: 'region' })
    collator = new Intl.Collator('en')
  }

  const options = allCountries
    .map((c) => {
      const code = c.iso2.toUpperCase()
      return {
        code,
        // `of` returns its argument for a region it does not know, which reads
        // as a bare code in the list rather than a blank row.
        name: display.of(code) ?? code,
        dialCode: c.dialCode,
      }
    })
    .sort((a, b) => collator.compare(a.name, b.name))

  cache.set(locale, options)
  return options
}

/** Dial code for a country, or `undefined` when it is not one we know. */
export function dialCodeFor(code: string): string | undefined {
  return allCountries.find((c) => c.iso2 === code.toLowerCase())?.dialCode
}
